import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../api/useAuth';
import { api } from '../api/client';
import { Stack, Row, Text, Input, Card, Button, Tag } from '../ui';

const SECTOR_OPTIONS = [
  'Energy','LNG','Oil & Gas','Power','Carbon',
  'Mining','Soft Commodities','Metals',
  'Banking','Capital Markets','Sovereign Capital','Funds',
  'Tech','Infrastructure','Government','Geopolitics','Other',
];

const TIMEZONES = [
  'America/Los_Angeles','America/Denver','America/Chicago','America/New_York','America/Sao_Paulo',
  'Europe/London','Europe/Zurich','Europe/Berlin','Africa/Lagos',
  'Asia/Dubai','Asia/Singapore','Asia/Tokyo','Asia/Hong_Kong','Australia/Sydney','UTC',
];

export function Onboarding() {
  const { friend, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(friend?.name ?? '');
  const [city, setCity] = useState(friend?.city ?? '');
  const [timezone, setTimezone] = useState(friend?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [sectors, setSectors] = useState<string[]>(friend?.sectors ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(s: string) {
    setSectors(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  }

  async function finish() {
    setBusy(true); setErr(null);
    try {
      await api.updateMe({ name: name.trim(), city: city.trim(), timezone, sectors });
      refresh();
      nav('/', { replace: true });
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  if (!friend) return null;

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-3">
      <Card className="w-full max-w-[560px]">
        <Stack gap={3}>
          <Stack gap={1}>
            <Row gap={1} align="center"><span className="inline-block h-3 w-3 bg-ink"></span><Text variant="title">welcome to your inner circle</Text></Row>
            <Text muted variant="small">a quick setup, then you're in. four short steps.</Text>
            <Row gap={0.5} className="mt-1">
              {[0,1,2,3].map(i => (
                <span key={i} className={`h-0.5 flex-1 ${i <= step ? 'bg-ink' : 'bg-hairline'}`} />
              ))}
            </Row>
          </Stack>

          {step === 0 && (
            <Stack gap={2}>
              <Text variant="heading">your name</Text>
              <Text variant="small" muted>this is what your friends see across the app.</Text>
              <Input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="full name" />
              <Row justify="end"><Button variant="primary" disabled={!name.trim()} onClick={() => setStep(1)}>next</Button></Row>
            </Stack>
          )}

          {step === 1 && (
            <Stack gap={2}>
              <Text variant="heading">where are you based</Text>
              <Text variant="small" muted>your home city. you can mark travel separately later.</Text>
              <Input autoFocus value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. san francisco" />
              <Row justify="between">
                <Button variant="ghost" onClick={() => setStep(0)}>back</Button>
                <Button variant="primary" disabled={!city.trim()} onClick={() => setStep(2)}>next</Button>
              </Row>
            </Stack>
          )}

          {step === 2 && (
            <Stack gap={2}>
              <Text variant="heading">your timezone</Text>
              <Text variant="small" muted>so the agents respect your quiet hours.</Text>
              <select value={timezone} onChange={e => setTimezone(e.target.value)} className="h-9 hairline rounded-sm px-2 bg-bg text-base">
                {TIMEZONES.map(t => <option key={t}>{t}</option>)}
              </select>
              <Row justify="between">
                <Button variant="ghost" onClick={() => setStep(1)}>back</Button>
                <Button variant="primary" onClick={() => setStep(3)}>next</Button>
              </Row>
            </Stack>
          )}

          {step === 3 && (
            <Stack gap={2}>
              <Text variant="heading">what sectors are you in?</Text>
              <Text variant="small" muted>pick everything that applies. the intel agent uses these to filter what surfaces in your morning brief.</Text>
              <Row gap={1} wrap>
                {SECTOR_OPTIONS.map(s => {
                  const on = sectors.includes(s);
                  return (
                    <button
                      key={s}
                      onClick={() => toggle(s)}
                      className={`h-9 sm:h-7 px-3 sm:px-2 rounded-sm text-xs transition-colors hairline ${on ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
                      {s}
                    </button>
                  );
                })}
              </Row>
              <Text variant="label">{sectors.length} selected</Text>
              {err && <Text variant="small" className="text-negative">{err}</Text>}
              <Row justify="between">
                <Button variant="ghost" onClick={() => setStep(2)}>back</Button>
                <Button variant="primary" onClick={finish} disabled={busy || sectors.length === 0}>{busy ? 'saving…' : 'finish & enter'}</Button>
              </Row>
            </Stack>
          )}
        </Stack>
      </Card>
    </div>
  );
}
